// Recipe-driven capability resolution for the dashboard shell. Pure
// functions, no React — testable without a jsdom pragma (the app has
// both jsdom and RTL available for component tests; see
// DelegationStatusControl.test.tsx for that shape). See ADR-0072.
//
// The pattern this replaces: screens defaulted to the facility branch
// while `useRecipe()` was still resolving (undefined), which fired a
// doomed /facility-dashboard request — and a 403 — for every
// non-facility org on first paint. Every capability check in this file
// treats "recipe not loaded yet" as its own state, never a fallback to
// a concrete vertical.

import {
  isFindingManagementRole,
  isUnitImportRole,
  type MyOrganizationResponse,
  type RecipeMeResponse,
  type Role,
} from "@bitacorapro/api-types";
import { isOrgAdminRole } from "@bitacorapro/core-engine";

export type HomeSurface = "loading" | "facility-overview" | "group-cartera" | "none";

export function isFacilityDashboardEnabled(recipe: RecipeMeResponse | undefined): boolean {
  return recipe?.dashboards?.facility === true;
}

export function isCarteraDashboardEnabled(recipe: RecipeMeResponse | undefined): boolean {
  return recipe?.dashboards?.cartera === true;
}

// Resolves which shell surface to render for `/`. Never branches on
// recipe.slug (§0.4(6)) — only on the dashboards capability primitives
// and rootEntity.
export function resolveHomeSurface(recipe: RecipeMeResponse | undefined): HomeSurface {
  if (recipe === undefined) return "loading";
  if (isFacilityDashboardEnabled(recipe)) return "facility-overview";
  if (recipe.rootEntity === "group") return "group-cartera";
  return "none";
}

// Whether the shell has any working surface for this recipe at all —
// the facility overview or the group cartera. `undefined` (recipe still
// loading) answers false here AND false in lacksDashboardSurface below:
// loading is its own state, never "has" and never "lacks".
export function hasDashboardSurface(recipe: RecipeMeResponse | undefined): boolean {
  const surface = resolveHomeSurface(recipe);
  return surface === "facility-overview" || surface === "group-cartera";
}

// True only once the recipe has loaded and resolves to no surface (an
// asset-rooted vertical with no `dashboards` key — residential_rental
// today). The shell then drops its nav and renders NoDashboardSurface
// instead of the routed screen. Never recipe.slug (ADR-0072).
export function lacksDashboardSurface(recipe: RecipeMeResponse | undefined): boolean {
  return resolveHomeSurface(recipe) === "none";
}

// Autogestión (plan 2026-10-30 §1.5): la receta declara el módulo
// Administración. Sin él, el menú y las rutas quedan como antes — el
// nav, los redirects de App.tsx y las migas leen este predicado, nunca
// recipe.slug.
export function hasAdminModule(recipe: RecipeMeResponse | undefined): boolean {
  return (recipe?.adminModule?.sections.length ?? 0) > 0;
}

// Autogestión (plan 2026-10-30 §1.1): quién administra la configuración
// de la organización. El administrador de plataforma y `admin` pasan
// siempre; además, el rol que la receta declara `orgAdmin` (el gerente de
// facility). Receta aún cargando → sólo los dos primeros, nunca un
// default de vertical. Es el espejo de `requireOrgAdmin` en core-api
// (lib/org-admin.ts): los gates de sección de abajo (share bundles,
// checklists, mantenciones) derivan de aquí, así la UI y el servidor
// no pueden discrepar sobre quién configura.
export type OrgAdminOpts = { platformAdmin?: boolean };

export function canAdministerOrg(
  recipe: RecipeMeResponse | undefined,
  role: string | undefined,
  opts: OrgAdminOpts = {}
): boolean {
  if (opts.platformAdmin === true) return true;
  if (role === "admin") return true;
  if (recipe === undefined || role === undefined) return false;
  // `memberRoles ?? []`: mismo resguardo que nav.ts ante una receta
  // parcial (fixtures de tests que sólo pueblan lo que leen).
  return isOrgAdminRole({ memberRoles: recipe.memberRoles ?? [] }, role);
}

// Share-bundle mint / revoke / access-log-read are gated on the wire to
// whoever administers the organization (core-api's share-bundles.ts via
// requireOrgAdmin). This mirrors that gate client-side so the FE never
// renders a control that fires a doomed 403 — same "UX-only, server
// re-checks" posture as RequireDashboardRole / RequirePlatformAdmin.
export function canManageShareBundles(
  recipe: RecipeMeResponse | undefined,
  role: Role | undefined,
  opts: OrgAdminOpts = {}
): boolean {
  return canAdministerOrg(recipe, role, opts);
}

// Quién LEE la nómina de contratistas (Administración · Contratistas): el
// espejo del gate de GET /providers en core-api (`isFindingManagementRole`
// + `assertProviderRosterLane`, lib/provider-roster.ts): `admin`, o un rol
// que la receta declara en el carril `assigning_party`, siempre que la
// receta tenga ciclo de observaciones. En facility eso incluye al
// supervisor, que no administra (sin `orgAdmin`) pero sí lee y amplía la
// nómina; por eso la sección no se gatea con canAdministerOrg —eso sólo
// esconde Editar y Desactivar (PATCH, requireOrgAdmin)—. Receta cargando
// → false, nunca un default de vertical. Misma firma que
// RequireRoleCapability; el administrador de plataforma no pasa sólo por
// serlo, igual que en el servidor.
export function canReadProviderRoster(
  recipe: Pick<RecipeMeResponse, "memberRoles" | "observationStatuses"> | undefined,
  role: string | undefined
): boolean {
  if (recipe === undefined || role === undefined) return false;
  if ((recipe.observationStatuses?.length ?? 0) === 0) return false;
  if (role === "admin") return true;
  if (!isFindingManagementRole(role)) return false;
  const lane = (recipe.memberRoles ?? []).find((r) => r.key === role)?.observationStatusActor;
  return lane === "assigning_party";
}

// ADR-0079. Gates the "checklists por tienda" admin panel — never on
// recipe.slug (§0.4(6)/ADR-0072), only on the per-reportType
// areaCatalogMode primitive. `recipe === undefined` (still loading)
// returns false here by design, same posture as
// isFacilityDashboardEnabled/isCarteraDashboardEnabled — callers that
// need to distinguish "loading" from "definitively absent" must check
// useRecipe().isLoading themselves, not infer it from this boolean.
export function isChecklistTemplateAdminEnabled(recipe: RecipeMeResponse | undefined): boolean {
  return recipe?.reportTypes.some((rt) => rt.areaCatalogMode === "org_checklist_template") ?? false;
}

// ADR-0079 Decision 7, re-keyed by autogestión §1.1 — mirrors core-api's
// requireOrgAdmin in checklist-templates.ts (before: the hardcoded
// ORG_SCOPED_ROLES). This is a whole-surface gate, not just a write
// gate: the backend's GET /checklist-templates* routes also run the same
// guard, so store_manager/brand_manager 403 on the read list too, not
// only on PUT/PATCH. Gate the panel's existence (and the `enabled` flag
// on every query it fires) on this, not just its write affordances — a
// read-only degrade for non-editors is not buildable, the backend never
// returns data to them.
export function canManageChecklistTemplates(
  recipe: RecipeMeResponse | undefined,
  role: Role | undefined,
  opts: OrgAdminOpts = {}
): boolean {
  return canAdministerOrg(recipe, role, opts);
}

// La sección Administración · Checklists (autogestión Fase 3, T-ljhq):
// existe sólo si la receta configura checklists por plantilla
// (isChecklistTemplateAdminEnabled) y es de quien administra la
// organización (canManageChecklistTemplates). Un solo predicado para que
// el menú (nav.ts, `allow`) y la ruta (App.tsx, RequireRoleCapability)
// nunca discrepen; `role` llega como string desde el menú y como Role
// desde la ruta, y canAdministerOrg acepta ambos.
export function canUseChecklistsSection(
  recipe: RecipeMeResponse | undefined,
  role: string | undefined,
  opts: OrgAdminOpts = {}
): boolean {
  return isChecklistTemplateAdminEnabled(recipe) && canAdministerOrg(recipe, role, opts);
}

// Catalog-configuration surface (create/patch items, assign assets) —
// mirrors core-api's requireOrgAdmin in maintenance.ts (autogestión
// §1.1), unchanged by ADR-0086, which only widened the mark-serviced
// lane below. The Mantenciones section of Administración reads this.
export function canManageMaintenanceItems(
  recipe: RecipeMeResponse | undefined,
  role: Role | undefined,
  opts: OrgAdminOpts = {}
): boolean {
  return canAdministerOrg(recipe, role, opts);
}

// ADR-0086 — mirrors core-api's MAINTENANCE_EVENT_WRITER_ROLES
// (maintenance.ts). This is the mark-serviced write gate specifically,
// split out from canManageMaintenanceItems (the catalog-config gate,
// above) once the two stopped sharing a role set: a service mark is a
// witness's attestation to a third-party contractor's visit, not a
// claim about the marker's own work, so store_manager/facility_supervisor
// are admitted here on their own asset scope — the server re-derives
// that scope per request regardless of what this function renders.
export function canMarkMaintenanceServiced(role: Role | undefined): boolean {
  return (
    role === "admin" ||
    role === "company_manager" ||
    role === "store_manager" ||
    role === "facility_supervisor"
  );
}

// ADR-0144 §2 — the unit importer (/carga-unidades). Mirrors core-api's
// /assets/import/* gate: the recipe declares `unitProfile` (never
// recipe.slug, §0.4(6)) and the caller is in UNIT_IMPORT_ROLE_KEYS or is
// a platform admin. `recipe === undefined` (still loading) is false, same
// posture as the other capability checks here.
export function canImportUnits(
  recipe: RecipeMeResponse | undefined,
  role: Role | undefined,
  platformAdmin: boolean | undefined
): boolean {
  if (!recipe?.unitProfile) return false;
  return isUnitImportRole(role ?? "") || platformAdmin === true;
}

// ADR-0173 fase 2 — the per-client AI context layer is a per-org flag a
// platform admin flips (`context_ingest`), never a recipe capability: the
// recipe says the vertical CAN carry client documents, the flag says this
// org may upload them today (D10). Absent flags (older core-api, still
// loading) read as off.
export function isContextLayerEnabled(org: MyOrganizationResponse | undefined): boolean {
  return org?.featureFlags?.context_ingest === true;
}

// Mirrors core-api's requireContextWrite (lib/context-access.ts): only
// org-scoped roles upload, review and activate; brand_manager reads its
// brand (the server gates that on the JWT brand claim).
export function canManageContextSources(role: Role | undefined): boolean {
  return role === "admin" || role === "company_manager";
}

// The brands the /contexto screen can show: every brand for an org-scoped
// caller (default first, as /organizations/me orders them), the caller's
// own brand for a brand-scoped one. Empty while the org row loads.
export function selectContextBrands(
  org: MyOrganizationResponse | undefined
): Array<{ id: string; name: string }> {
  if (!org) return [];
  if (org.brands && org.brands.length > 0)
    return org.brands.map((b) => ({ id: b.id, name: b.name }));
  if (org.callerBrand) return [{ id: org.callerBrand.id, name: org.callerBrand.name }];
  return [];
}
